import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import info from '../../assets/icon/in.png';
import giftIcon from '../../assets/icon/git_outline.png';
import walletIcon from '../../assets/icon/wallet.png';
import inviteFriendTaskIcon from '../../assets/icon/invite_friend_task_icon.png';
import Avatar from "../avatar/Avatar";
import getIcon from "../../utils/Icon";
import TaskApi from "../../api/taskApi";
import PromosApi from "../../api/PromosApi";
import loginApi from "../../api/loginApi";
import awardApi from "../../api/awardApi";
import referralApi from "../../api/referralApi";
import tokenApi from "../../api/tokenApi";
import CrystalsApi from "../../api/CrystalsApi";
import { executeClaim } from "../../utils/claimOnChain";
import ClaimRoulette from "../ClaimRoulette/ClaimRoulette";
import { pushBackHandler, popBackHandler } from "../../utils/backHandler";

// Дефолтные таски, отключённые промоутером для ЭТОГО промо, из прогресса убираем.
// Отключаемы только is_locked=false — то же правило проверяет сервер при клейме.
const filterDefaultsForPromo = (progress, defaults, promo) => {
    const disabled = new Set((promo?.disabledDefaultTaskIds || []).map(Number));
    if (!progress || !disabled.size) return progress;
    const lockedIds = new Set(
        (defaults || []).filter((t) => t.is_locked !== false).map((t) => Number(t.id)),
    );
    const excluded = (t) => disabled.has(Number(t.id)) && !lockedIds.has(Number(t.id));
    const completed = (progress.completed_tasks || []).filter((t) => !excluded(t));
    const uncompleted = (progress.uncompleted_tasks || []).filter((t) => !excluded(t));
    return {
        ...progress,
        completed_tasks: completed,
        uncompleted_tasks: uncompleted,
        completed_count: completed.length,
        uncompleted_count: uncompleted.length,
        all_completed: uncompleted.length === 0,
    };
};

export default function Tasks(){
    const [isSubscribe, setSubscribe] = useState(false);
    const [isOwner, setIsOwner] = useState(false);
    // Временно: владелец видит задания/прогресс/клейм в своём промо (тестирует
    // полный цикл). Убрать вместе с запретом «создатель не участвует» на бэке.
    const OWNER_PARTICIPATES = true;
    const ownerBlocked = isOwner && !OWNER_PARTICIPATES;
    const [checkingStatus, setCheckingStatus] = useState(true);
    const [promoTasks, setPromoTasks] = useState([]);
    const [tasksLoading, setTasksLoading] = useState(false);
    const [subscribing, setSubscribing] = useState(false);
    
    // Прогресс задач
    const [completedTasksList, setCompletedTasksList] = useState([]);
    const [uncompletedTasksList, setUncompletedTasksList] = useState([]);
    const [progressLoading, setProgressLoading] = useState(false);
    
    // Claim состояния
    const [claiming, setClaiming] = useState(false);
    const [claimResult, setClaimResult] = useState(null); // 'success' | 'failed' | null
    const [claimError, setClaimError] = useState('');
    const [alreadyClaimed, setAlreadyClaimed] = useState(false);
    
    // Второй шанс
    const [canSecondChance, setCanSecondChance] = useState(false);
    const [promoReferralLink, setPromoReferralLink] = useState('');
    
    // Default tasks
    const [defaultProgress, setDefaultProgress] = useState(null);
    const [inviteCheck, setInviteCheck] = useState(null);
    
    // Текущий пользователь
    const [currentUserId, setCurrentUserId] = useState(null);
    
    // Roulette state
    const [showRoulette, setShowRoulette] = useState(false);
    const [rouletteData, setRouletteData] = useState(null); // { winnerPosition, winnerAmount }
    const [loadedCrystals, setLoadedCrystals] = useState([]);
    const [tokenIconUrl, setTokenIconUrl] = useState('');
    const [tokenTicker, setTokenTicker] = useState('');
    
    // Existing award (if user already claimed)
    const [existingAward, setExistingAward] = useState(null);
    
    // Blockchain claim state
    const [blockchainSignature, setBlockchainSignature] = useState(null);
    const [blockchainClaimLoading, setBlockchainClaimLoading] = useState(false);
    const [blockchainClaimError, setBlockchainClaimError] = useState(null);
    
    // Получаем promoId из Redux
    const promo = useSelector((state) => state.selectedPromo.promo);
    const promoId = promo?.id;
    const promoType = promo?.promoType || 'crystalsAndCrypto';
    // Признак — любой on-chain след, не только chainId: форма иногда не проставляет
    // его при создании, и победитель не видел кнопку он-чейн клейма.
    const isBlockchainPromo = !!(promo?.chainId || promo?.depositTxHash || promo?.tokenContractAddress);
    const promoPositions = promo?.category?.positions || [];
    
    // Проверяем владельца и подписку
    useEffect(() => {
        if (!promoId) return;
        
        setCheckingStatus(true);
        
        Promise.all([
            loginApi.me().catch((e) => { console.error('[Tasks] me() error:', e); return null; }),
            PromosApi.UserPromos.isSubscribed(promoId).catch((e) => { console.error('[Tasks] isSubscribed error:', e); return false; })
        ]).then(([meData, subscribed]) => {
            const currentUser = meData?.user || meData;
            const userId = Number(currentUser?.id);
            const creatorId = Number(promo?.creatorUserId);
            
            setCurrentUserId(userId);
            
            console.log('[Tasks] Status check:', { userId, creatorId, subscribed });
            
            if (userId && creatorId && userId === creatorId) {
                setIsOwner(true);
                setSubscribe(true);
            } else {
                setIsOwner(false);
                setSubscribe(subscribed);
            }
        }).finally(() => {
            setCheckingStatus(false);
        });
    }, [promoId, promo]);
    
    // Загружаем задачи для промо
    useEffect(() => {
        if (!promoId) return;
        
        setTasksLoading(true);
        TaskApi.getTasksByPromo(promoId)
            .then(tasks => {
                console.log('[Tasks] loaded tasks:', tasks);
                setPromoTasks(tasks || []);
            })
            .catch(err => {
                console.error('[Tasks] failed to load:', err);
                setPromoTasks([]);
            })
            .finally(() => setTasksLoading(false));
    }, [promoId]);
    
    // Загружаем прогресс пользователя по задачам
    const fetchProgress = useCallback(async () => {
        if (!promoId || ownerBlocked) return;
        
        setProgressLoading(true);
        try {
            const [promoData, defaultData, defaultsList] = await Promise.all([
                TaskApi.getMyProgress(promoId),
                TaskApi.getDefaultTasksProgress().catch(() => null),
                TaskApi.getDefaultTasks().catch(() => null),
            ]);
            console.log('[Tasks] progress:', promoData, 'default:', defaultData);
            setCompletedTasksList(promoData?.completed_tasks || []);
            setUncompletedTasksList(promoData?.uncompleted_tasks || []);
            if (defaultData) setDefaultProgress(filterDefaultsForPromo(defaultData, defaultsList, promo));
        } catch (err) {
            console.error('[Tasks] progress error:', err);
        } finally {
            setProgressLoading(false);
        }
    }, [promoId, ownerBlocked]);
    
    useEffect(() => {
        if ((isSubscribe || isOwner) && !ownerBlocked && promoId) {
            fetchProgress();
        }
    }, [isSubscribe, isOwner, ownerBlocked, promoId, fetchProgress]);
    
    // Check if user already has an award for this promo
    useEffect(() => {
        if (!promoId || ownerBlocked || !currentUserId) return;
        
        awardApi.getUserDrops(1, 50)
            .then(data => {
                const items = Array.isArray(data) ? data : (data?.items || data?.data || []);
                const myAward = items.find(a => Number(a.promoId) === Number(promoId));
                if (myAward) {
                    console.log('[Tasks] existing award found:', myAward);
                    setExistingAward(myAward);
                    setAlreadyClaimed(true);
                }
            })
            .catch(err => console.error('[Tasks] check existing award error:', err));
    }, [promoId, isOwner, currentUserId]);
    
    // Pre-load crystals and token for roulette
    useEffect(() => {
        if (!promoId || ownerBlocked) return;
        
        // Load crystals for positions (for onlyCrystals and crystalsAndCrypto)
        if (promoType !== 'cryptoOnly' && promoPositions.length > 0) {
            const posArray = Array.from({ length: promoPositions.length }, (_, i) => i + 1);
            CrystalsApi.getGemsByPositions(posArray)
                .then(res => {
                    console.log('[Tasks] roulette crystals:', res);
                    setLoadedCrystals(res || []);
                })
                .catch(() => setLoadedCrystals([]));
        }
        
        // Load token logo
        const tokenType = promo?.tokenType;
        if (tokenType) {
            tokenApi.lookupToken(tokenType, 't')
                .then(res => {
                    const logoPath = res?.logoPath || '';
                    setTokenIconUrl(logoPath ? `https://api.duotetra.quest/${logoPath}` : '');
                    setTokenTicker(res?.symbol || res?.name || '');
                })
                .catch(() => { setTokenIconUrl(''); setTokenTicker(''); });
        }
    }, [promoId, isOwner, promoType, promoPositions.length, promo?.tokenType]);
    
    // Проверяем invite requirement и второй шанс
    useEffect(() => {
        if (!promoId || ownerBlocked) return;
        
        referralApi.checkInviteRequirement(promoId)
            .then(res => {
                console.log('[Tasks] inviteCheck:', res);
                setInviteCheck(res);
            })
            .catch(() => setInviteCheck(null));
        
        referralApi.canClaimSecond(promoId)
            .then(res => {
                console.log('[Tasks] canClaimSecond:', res);
                setCanSecondChance(!!res?.canClaim);
            })
            .catch(() => setCanSecondChance(false));
        
        // Ссылка для задачи «пригласи друга» — всегда приглашение участника.
        // Без явного типа создатель промо получил бы ссылку промоутера, а по ней
        // друг не подписывается и требование клейма не засчитывается.
        referralApi.getReferralLink(promoId, 'PROMO_INVITATION')
            .then(res => {
                console.log('[Tasks] referral link:', res);
                setPromoReferralLink(res?.link || res?.referralLink || '');
            })
            .catch(() => setPromoReferralLink(''));
    }, [promoId, isOwner]);
    
    // Подписка на промо
    const handleSubscribe = async () => {
        if (!promoId || subscribing) return;
        
        setSubscribing(true);
        try {
            await PromosApi.UserPromos.subscribe(promoId);
            console.log('[Tasks] subscribed to promo:', promoId);
            setSubscribe(true);
            // Подписка открывает право на ссылку-приглашение — забираем её сразу
            referralApi.getReferralLink(promoId, 'PROMO_INVITATION')
                .then(res => setPromoReferralLink(res?.link || res?.referralLink || ''))
                .catch(() => {});
        } catch (err) {
            console.error('[Tasks] subscribe error:', err);
        } finally {
            setSubscribing(false);
        }
    };
    
    // Claim награды
    const handleClaim = async (isRetry = false) => {
        if (!promoId || !currentUserId || claiming) return;
        
        setClaiming(true);
        setClaimResult(null);
        setClaimError('');
        
        try {
            const result = await awardApi.issue({
                promoId,
                userId: currentUserId,
                referralRetry: isRetry,
            });
            console.log('[Tasks] claim result:', result);
            
            // Open roulette animation with winning data
            setRouletteData({
                winnerPosition: result?.position || 1,
                winnerAmount: result?.amount || 0,
            });
            setShowRoulette(true);
            // Don't set claimResult yet — will set after roulette closes
        } catch (err) {
            console.error('[Tasks] claim error:', err);
            const msg = err?.response?.data?.message || 'Claim failed';
            
            if (msg.includes('already received')) {
                setAlreadyClaimed(true);
                setClaimError('You already received an award for this promo');
            } else if (msg.includes('не попали в пул') || err?.response?.status === 403) {
                setClaimResult('failed');
                setClaimError(msg);
            } else if (msg.includes('Default tasks not completed')) {
                setClaimError('Complete all default tasks first');
            } else if (msg.includes('invite a friend') || msg.includes('invited friend')) {
                setClaimError('Invite a friend who completes at least one promo task');
            } else {
                setClaimError(msg);
            }
        } finally {
            setClaiming(false);
        }
    };
    
    // Handle roulette close
    const handleRouletteClose = () => {
        setShowRoulette(false);
        setRouletteData(null);
        setClaimResult('success');
        setAlreadyClaimed(true);
    };
    
    // Show existing award in roulette (when user already claimed)
    const handleShowExistingAward = () => {
        if (!existingAward) return;
        
        setRouletteData({
            winnerPosition: existingAward.position || 1,
            winnerAmount: existingAward.amount || 0,
        });
        setShowRoulette(true);
    };
    
    // Подсчёт прогресса (промо + дефолтные)
    const promoTotal = completedTasksList.length + uncompletedTasksList.length;
    const promoCompleted = completedTasksList.length;
    const allPromoTasksDone = promoTotal > 0 && uncompletedTasksList.length === 0;
    
    const defaultTotal = (defaultProgress?.completed_count || 0) + (defaultProgress?.uncompleted_count || 0);
    const defaultCompleted = defaultProgress?.completed_count || 0;
    const allDefaultTasksDone = defaultProgress?.all_completed || false;
    
    const totalTasks = promoTotal + defaultTotal;
    const completedCount = promoCompleted + defaultCompleted;
    
    const inviteRequirementMet = inviteCheck?.eligible || false;
    
    // All conditions for claim: all promo tasks + all default tasks + invite requirement
    const allTasksDone = allPromoTasksDone && allDefaultTasksDone && inviteRequirementMet;

    // Дефолтный таск «Invite friend» закрывается не тапом, а фактом зачтённого
    // приглашения — фиксируем его в прогрессе автоматически. Без этого
    // all_completed не наступает никогда и клейм вечно заблокирован.
    useEffect(() => {
        if (!inviteRequirementMet) return;
        const inviteTask = (defaultProgress?.uncompleted_tasks || []).find(
            t => t.type === 'invite_friend' || t.icon === 'invite'
        );
        if (!inviteTask) return;
        TaskApi.completeDefaultTask(inviteTask.id)
            .then(() => fetchProgress())
            .catch(() => {});
    }, [inviteRequirementMet, defaultProgress, fetchProgress]);


    const Sub = ()=><div className={'flex flex-col items-center w-full gap-[22px] '}>
    <div className={'w-full flex flex-row items-center justify-center'}>
    <span className={'text-[11px] text-[#959595] font-semibold'}>
    complete all tasks to claim your reward
    </span>
    </div>
    <div className={'w-full flex flex-row items-center px-[15px] relative'}>
        <img width={15} height={15} src={info} alt="info" className="absolute left-[15px]"/>
        <div className={'w-full flex flex-row justify-center'}>
            <div className={'min-w-[42px] h-[25px]  bg-white rounded-[10px] flex flex-row items-center justify-center'}>
                <span className={'text-black text-[12px] font-semibold whitespace-nowrap'}>0/0</span>
            </div>
        </div>
        <div className={'absolute right-[15px] cursor-pointer py-[6px] px-[22px] flex flex-row items-center justify-center rounded-[8px] bg-[#353232]'}>
                <img width={25} height={25} src={giftIcon} alt="gift"  />
            </div>
    </div>
    <div className={'w-full  flex flex-row items-center justify-center '}>
            <div className={`w-[132px] h-[31px] bg-[#FFC813] cursor-pointer rounded-[4px] flex flex-row items-center justify-center ${subscribing ? 'opacity-50' : ''}`} onClick={handleSubscribe}>
                    <span className={'text-[#312706] text-[14px] font-bold'}>{subscribing ? 'Joining...' : "Let's go"}</span>
            </div>
    </div>
</div>

    // Плашка прогресса задач и Claim
    const ProgressBar = () => (
        <div className={'flex flex-col items-center w-full gap-[14px] py-[12px]'}>
            {/* Статус текст */}
            <div className={'w-full flex flex-col items-center justify-center gap-[2px]'}>
                <span className={'text-[11px] text-[#959595] font-semibold'}>
                    {alreadyClaimed 
                        ? (existingAward ? 'Tap "My reward" to see what you won' : 'You already claimed this promo')
                        : allTasksDone 
                            ? 'All tasks completed! Claim your reward' 
                            : 'Complete all requirements to claim'}
                </span>
                {!alreadyClaimed && !allTasksDone && (
                    <div className="flex flex-row flex-wrap items-center justify-center gap-x-3 gap-y-1 mt-1">
                        <span className={`text-[10px] ${allDefaultTasksDone ? 'text-[#4ade80]' : 'text-[#f87171]'}`}>
                            {allDefaultTasksDone ? '✓' : '✗'} Default tasks
                        </span>
                        <span className={`text-[10px] ${allPromoTasksDone ? 'text-[#4ade80]' : 'text-[#f87171]'}`}>
                            {allPromoTasksDone ? '✓' : '✗'} Promo tasks
                        </span>
                        <span className={`text-[10px] ${inviteRequirementMet ? 'text-[#4ade80]' : 'text-[#f87171]'}`}>
                            {inviteRequirementMet ? '✓' : '✗'} Invite friend
                        </span>
                    </div>
                )}
            </div>
            
            {/* Прогресс бар */}
            <div className={'w-full flex flex-row items-center px-[15px] relative'}>
                <img width={15} height={15} src={info} alt="info" className="absolute left-[15px]"/>
                <div className={'w-full flex flex-row justify-center'}>
                    <div className={'min-w-[42px] h-[25px] bg-white rounded-[10px] flex flex-row items-center justify-center'}>
                        <span className={'text-black text-[12px] font-semibold whitespace-nowrap'}>
                            {completedCount}/{totalTasks}
                        </span>
                    </div>
                </div>
                
                {/* Кнопка Claim (gift) */}
                {allTasksDone && !alreadyClaimed && claimResult !== 'failed' && (
                    <div 
                        className={`absolute right-[15px] cursor-pointer py-[6px] px-[16px] flex flex-row items-center justify-center rounded-[8px] ${claiming ? 'bg-[#252525] opacity-50' : 'bg-[#FFC813]'}`}
                        onClick={() => handleClaim(false)}
                    >
                        <img width={20} height={20} src={giftIcon} alt="gift" />
                        <span className={'text-black text-[12px] font-bold ml-[6px]'}>
                            {claiming ? '...' : 'Claim'}
                        </span>
                    </div>
                )}
                
                {/* Уже получил награду — кликабельный для показа выигрыша */}
                {alreadyClaimed && (
                    <div 
                        className={'absolute right-[15px] py-[6px] px-[16px] flex flex-row items-center justify-center rounded-[8px] bg-[#2a5a2a] cursor-pointer active:scale-95 transition-transform'}
                        onClick={existingAward ? handleShowExistingAward : undefined}
                    >
                        <img width={16} height={16} src={giftIcon} alt="gift" className="mr-[4px] opacity-80" />
                        <span className={'text-[#4ade80] text-[12px] font-bold'}>
                            {existingAward ? 'My reward' : 'Claimed'}
                        </span>
                    </div>
                )}
            </div>
            
            {/* Результат Claim — успех */}
            {claimResult === 'success' && (
                <div className={'w-full flex flex-col items-center gap-[8px] px-[15px] py-[10px] bg-[#1a2e1a] rounded-[12px] mx-[15px]'}>
                    <span className={'text-[24px]'}>🎉</span>
                    <span className={'text-[#4ade80] text-[14px] font-bold'}>Congratulations!</span>
                    <span className={'text-[#959595] text-[12px] text-center'}>
                        {isBlockchainPromo
                            ? 'You won! Claim your tokens on-chain below.'
                            : 'You won a reward! Check your Drops tab.'}
                    </span>
                    {isBlockchainPromo && !blockchainSignature && (
                        <div
                            className={`mt-1 px-[16px] py-[6px] rounded-[8px] cursor-pointer flex items-center gap-[6px] ${blockchainClaimLoading ? 'bg-[#252525] opacity-50' : 'bg-[#FFC813]'}`}
                            onClick={async () => {
                                if (blockchainClaimLoading) return;
                                setBlockchainClaimError(null);
                                setBlockchainClaimLoading(true);
                                try {
                                    const award = existingAward || rouletteData;
                                    const humanAmount = String(award?.winnerAmount || award?.amount || '0');
                                    // Нормализация регистра (EIP-55): в базе адрес бывает битый
                                    const tokenAddress = String(promo?.tokenContractAddress || promo?.tokenType || '').toLowerCase();
                                    const result = await executeClaim({
                                        promoId,
                                        humanAmount,
                                        tokenAddress,
                                    });
                                    setBlockchainSignature({
                                        signature: result.signature,
                                        txHash: result.txHash,
                                        winnerAddress: result.winnerAddress,
                                    });
                                } catch (err) {
                                    console.error('Blockchain claim error:', err);
                                    const msg = err?.response?.data?.message
                                        || err?.info?.error?.message
                                        || err?.shortMessage
                                        || err?.reason
                                        || err?.message
                                        || 'Claim failed';
                                    setBlockchainClaimError(Array.isArray(msg) ? msg.join(', ') : String(msg));
                                } finally {
                                    setBlockchainClaimLoading(false);
                                }
                            }}
                        >
                            <img width={16} height={16} src={walletIcon} alt="wallet" />
                            <span className='text-black text-[12px] font-bold'>
                                {blockchainClaimLoading ? '...' : 'Get Claim Signature'}
                            </span>
                        </div>
                    )}
                    {blockchainClaimError && (
                        <div className='w-full mt-1 px-2 py-2 rounded-[8px] border border-rose-400/20 bg-rose-500/10'>
                            <span className='text-[11px] text-rose-300 break-words'>{blockchainClaimError}</span>
                        </div>
                    )}
                    {isBlockchainPromo && blockchainSignature && (
                        <div className='w-full mt-1 flex flex-col gap-[4px]'>
                            <span className='text-[10px] text-[#959595]'>Signature (use in smart contract):</span>
                            <div className='w-full bg-[#121212] border border-[#353232] rounded-[4px] px-2 py-1 break-all'>
                                <span className='text-[9px] text-[#4ade80] font-mono'>{blockchainSignature.signature}</span>
                            </div>
                        </div>
                    )}
                </div>
            )}
            
            {/* Результат Claim — не прошёл лотерею */}
            {claimResult === 'failed' && (
                <div className={'w-full flex flex-col items-center gap-[10px] px-[15px] py-[12px] bg-[#2a1a1a] rounded-[12px] mx-[15px]'}>
                    <span className={'text-[24px]'}>😔</span>
                    <span className={'text-[#f87171] text-[14px] font-bold'}>Not this time...</span>
                    <span className={'text-[#959595] text-[12px] text-center'}>
                        You didn't win in the lottery pool. But you have a second chance!
                    </span>
                    <span className={'text-[#FFC813] text-[12px] font-semibold text-center'}>
                        Invite friends via your referral link to get another try!
                    </span>
                </div>
            )}
            
            {/* Ошибка claim (не лотерея) */}
            {claimError && claimResult !== 'failed' && !alreadyClaimed && (
                <div className={'w-full px-[15px]'}>
                    <span className={'text-[#f87171] text-[11px]'}>{claimError}</span>
                </div>
            )}
        </div>
    )


const TaskItem = ({avatar, label, text, link}) => {
    const faviconUrl = getFaviconUrl(link);
    
    const handleClick = () => {
        if (link) {
            window.open(link, '_blank', 'noopener,noreferrer');
        }
    };
    
    return (
        <div className={'flex flex-row items-center border-b border-[#353232] justify-between w-full py-4 px-2'}>
            <div className={'flex flex-row items-center justify-start gap-4'}>
                {faviconUrl ? (
                    <div className="w-[40px] h-[40px] rounded-full overflow-hidden flex-shrink-0">
                        <img 
                            src={faviconUrl} 
                            alt="site icon" 
                            className="w-full h-full object-cover rounded-full"
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.parentElement.innerHTML = '';
                            }}
                        />
                    </div>
                ) : (
                    <Avatar width={40} height={40} icon={avatar} label={label}/>
                )}
                <span className={'text-[#EAEAEA] text-[15px] font-semibold'}>{text}</span>
            </div>
            <div 
                className={'flex flex-row items-center justify-center rounded-[10px] w-[56px] h-[36px] bg-[#FFC813] cursor-pointer'}
                onClick={handleClick}
            >
                <span className={'text-black text-[13px]'}>Go</span>
            </div>
        </div>
    );
}

const DefaultTasks = ({ onOpenInviteModal }) => {
    const [completing, setCompleting] = useState({});
    
    const completedIds = new Set(
        (defaultProgress?.completed_tasks || []).map(t => t.id)
    );
    const allDefaults = [
        ...(defaultProgress?.completed_tasks || []),
        ...(defaultProgress?.uncompleted_tasks || []),
    ].sort((a, b) => a.id - b.id);

    const hasInviteFromApi = allDefaults.some(
        (t) => t.type === 'invite_friend' || t.icon === 'invite'
    );
    const tasksToShow = hasInviteFromApi
        ? allDefaults
        : [
            ...allDefaults,
            {
                id: 'invite-friend-local',
                title: 'Invite friend',
                type: 'invite_friend',
                icon: 'invite',
            },
        ];

    const [verifyError, setVerifyError] = useState({});

    const openTaskLink = (link, taskType) => {
        if (!link) return;
        const webApp = window.Telegram?.WebApp;
        if (taskType === 'telegram_subscribe' && link.includes('t.me/') && webApp?.openTelegramLink) {
            webApp.openTelegramLink(link);
        } else {
            window.open(link, '_blank', 'noopener,noreferrer');
        }
    };

    const handleCompleteDefault = async (taskId, link, taskType) => {
        if (completedIds.has(taskId)) {
            openTaskLink(link, taskType);
            return;
        }

        setVerifyError(prev => ({ ...prev, [taskId]: null }));
        setCompleting(prev => ({ ...prev, [taskId]: true }));

        try {
            await TaskApi.completeDefaultTask(taskId);
            setVerifyError(prev => ({ ...prev, [taskId]: null })); 
            await fetchProgress();
            openTaskLink(link, taskType);
        } catch (err) {
            const msg = err?.response?.data?.message || err?.message || 'Failed to complete task';
            if (taskType === 'telegram_subscribe' && msg.toLowerCase().includes('subscribe')) {
                setVerifyError(prev => ({ ...prev, [taskId]: 'Subscribe first, then tap Check' }));
                openTaskLink(link, taskType);
            } else {
                setVerifyError(prev => ({ ...prev, [taskId]: msg }));
            }
            console.error('[Tasks] default complete error:', err);
        } finally {
            setCompleting(prev => ({ ...prev, [taskId]: false }));
        }
    };

    const cleanTgLink = (link) => {
        if (!link) return null;
        const match = link.match(/(?:t\.me|telegram\.me)\/([a-zA-Z0-9_]+)/);
        return match ? `t.me/${match[1]}` : null;
    };

    const getOpenUrl = (task) => {
        if (!task.link) return null;
        if (task.type === 'telegram_subscribe') {
            const tg = cleanTgLink(task.link);
            return tg ? `https://${tg}` : task.link;
        }
        return task.link;
    };

    if (!allDefaults.length) {
        return (
            <div className="flex flex-col items-center w-full">
                <TaskItem text="Follow OctopusSQD official X" label="twitter" />
                <TaskItem text="Follow OctopusSQD channel" label="telegram" />
                <div
                    className="flex flex-row items-center border-b border-[#353232] justify-between w-full py-4 px-2"
                    style={{ backgroundColor: inviteRequirementMet ? 'rgba(74, 222, 128, 0.04)' : 'transparent' }}
                >
                    <div className="flex flex-row items-center justify-start gap-4 flex-1 min-w-0">
                        <div className="w-[40px] h-[40px] min-w-[40px] rounded-full flex items-center justify-center" style={{ background: '#D3D3D3' }}>
                            <img width={24} height={24} src={inviteFriendTaskIcon} alt="" />
                        </div>
                        <span
                            className="text-[15px] font-semibold"
                            style={{ color: inviteRequirementMet ? '#4ade80' : '#EAEAEA', textDecoration: inviteRequirementMet ? 'line-through' : 'none' }}
                        >
                            Invite friend
                        </span>
                    </div>
                    {inviteRequirementMet ? (
                        <div className="w-[56px] h-[36px] flex items-center justify-center rounded-[10px] bg-[#2a5a2a] flex-shrink-0">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                <path d="M5 13l4 4L19 7" stroke="#4ade80" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </div>
                    ) : (
                        <div
                            className="w-[66px] h-[36px] flex items-center justify-center bg-[#FFC813] rounded-[10px] cursor-pointer flex-shrink-0"
                            onClick={() => onOpenInviteModal?.()}
                        >
                            <span className="text-[13px] text-black font-semibold">Invite</span>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col items-center w-full">
            {tasksToShow.map(task => {
                const isInvite = task.type === 'invite_friend' || task.icon === 'invite';
                const isTg = task.type === 'telegram_subscribe';
                const isDone = isInvite ? inviteRequirementMet : completedIds.has(task.id);
                const isLoading = completing[task.id];

                const displayLink = isTg ? cleanTgLink(task.link) : task.link;

                const inviteSubtext = isInvite
                    ? (!inviteCheck?.hasInvitedFriend
                        ? 'Invite a friend to claim'
                        : !inviteCheck?.friendCompletedTask
                            ? 'Waiting for friend to complete a task...'
                            : 'Friend completed a task!')
                    : null;
                
                return (
                    <div
                        key={task.id}
                        className="flex flex-row items-center border-b border-[#353232] justify-between w-full py-4 px-2"
                        style={{ backgroundColor: isDone ? 'rgba(74, 222, 128, 0.04)' : 'transparent' }}
                    >
                        <div className="flex flex-row items-center justify-start gap-4 flex-1 min-w-0">
                            {isInvite ? (
                                <div className="w-[40px] h-[40px] min-w-[40px] rounded-full flex items-center justify-center" style={{ background: '#D3D3D3' }}>
                                    <img width={24} height={24} src={inviteFriendTaskIcon} alt="" />
                                </div>
                            ) : task.icon === 'wallet' ? (
                                <img width={40} height={40} src={walletIcon} className="rounded-full flex-shrink-0" alt="" />
                            ) : (
                                <Avatar width={40} height={40} icon={getIcon('octo')} label={task.icon || 'telegram'} />
                            )}
                            <div className="flex flex-col min-w-0">
                                <span
                                    className="text-[15px] font-semibold truncate"
                                    style={{ color: isDone ? '#4ade80' : '#EAEAEA', textDecoration: isDone ? 'line-through' : 'none' }}
                                >
                                    {task.title}
                                </span>
                                {isInvite ? (
                                    <span className="text-[10px] text-[#959595]">{inviteSubtext}</span>
                                ) : verifyError[task.id] ? (
                                    <span className="text-[10px] text-[#f87171]">{verifyError[task.id]}</span>
                                ) : displayLink ? (
                                    <span className="text-[#959595] text-[11px] truncate">{displayLink}</span>
                                ) : null}
                            </div>
                        </div>

                        {isInvite ? (
                            isDone ? (
                                <div className="w-[56px] h-[36px] flex items-center justify-center rounded-[10px] bg-[#2a5a2a] flex-shrink-0">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                        <path d="M5 13l4 4L19 7" stroke="#4ade80" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </div>
                            ) : (
                                <div
                                    className="w-[66px] h-[36px] flex items-center justify-center bg-[#FFC813] rounded-[10px] cursor-pointer flex-shrink-0"
                                    onClick={() => onOpenInviteModal?.()}
                                >
                                    <span className="text-[13px] text-black font-semibold">Invite</span>
                                </div>
                            )
                        ) : (
                            <div
                                className="flex flex-row items-center justify-center rounded-[10px] flex-shrink-0 cursor-pointer"
                                style={{
                                    backgroundColor: isDone ? '#2a5a2a' : verifyError[task.id] ? '#2B8FF4' : '#FFC813',
                                    width: verifyError[task.id] ? 66 : 56,
                                    height: 36,
                                }}
                                onClick={() => !isDone && handleCompleteDefault(task.id, getOpenUrl(task), task.type)}
                            >
                                {isLoading ? (
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ animation: 'spin 0.8s linear infinite' }}>
                                        <circle cx="12" cy="12" r="10" stroke="#000" strokeWidth="3" strokeLinecap="round" strokeDasharray="31.4 31.4" />
                                    </svg>
                                ) : isDone ? (
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                        <path d="M5 13l4 4L19 7" stroke="#4ade80" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                ) : verifyError[task.id] ? (
                                    <span className="text-white text-[13px] font-semibold">Check</span>
                                ) : (
                                    <span className="text-black text-[13px] font-semibold">Go</span>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

// Модалка приглашения друзей (выезжает снизу)
const InviteModal = ({ isOpen, onClose, referralLink }) => {
    const [copied, setCopied] = useState(false);

    // Перехват Telegram BackButton — закрыть модалку при нажатии "назад"
    const handleCloseModal = useCallback(() => {
        if (onClose) onClose();
    }, [onClose]);

    useEffect(() => {
        if (!isOpen) return;
        pushBackHandler(handleCloseModal);
        return () => popBackHandler(handleCloseModal);
    }, [isOpen, handleCloseModal]);
    
    const handleCopyLink = () => {
        if (referralLink) {
            navigator.clipboard.writeText(referralLink);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };
    
    const handleShareTelegram = () => {
        const tg = window.Telegram?.WebApp;
        if (!tg || !referralLink) return;
        
        const text = `Join me in this promo! 🎁`;
        const url = referralLink;

        // Только t.me/share/url: switchInlineQuery требует, чтобы бот отвечал
        // на inline-запросы — наш не отвечает, и юзер упирался в вечный лоадер.
        tg.openTelegramLink(`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`);
    };
    
    if (!isOpen) return null;
    
    return (
        <>
            {/* Overlay */}
            <div 
                className="fixed inset-0 bg-black/60 z-40"
                onClick={onClose}
            />
            {/* Modal */}
            <div className="fixed bottom-0 left-0 right-0 z-50 bg-[#1a1a1a] rounded-t-[20px] border-t border-[#353232] animate-slide-up">
                {/* Header */}
                <div className="flex flex-row items-center justify-between px-4 py-3 border-b border-[#353232]">
                    <div className="w-[40px]">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#959595" strokeWidth="2">
                            <circle cx="11" cy="11" r="8"/>
                            <path d="m21 21-4.35-4.35"/>
                        </svg>
                    </div>
                    <div className="flex flex-col items-center">
                        <span className="text-white text-[16px] font-semibold">Invite friends</span>
                        <span className="text-[#959595] text-[12px]">Select a friend</span>
                    </div>
                    <div 
                        className="w-[40px] flex justify-end cursor-pointer"
                        onClick={handleShareTelegram}
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#5BC2E7" strokeWidth="2">
                            <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
                            <polyline points="16 6 12 2 8 6"/>
                            <line x1="12" y1="2" x2="12" y2="15"/>
                        </svg>
                    </div>
                </div>
                
                {/* Share via Telegram button */}
                <div className="px-4 py-6">
                    <button 
                        onClick={handleShareTelegram}
                        className="w-full py-4 bg-[#0088cc] rounded-[12px] flex flex-row items-center justify-center gap-3"
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="#EAEAEA">
                            <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18 1.897-.962 6.502-1.359 8.627-.168.9-.5 1.201-.82 1.23-.697.064-1.226-.461-1.901-.903-1.056-.692-1.653-1.123-2.678-1.799-1.185-.781-.417-1.21.258-1.911.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.139-5.062 3.345-.479.329-.913.489-1.302.481-.428-.009-1.252-.242-1.865-.442-.751-.244-1.349-.374-1.297-.789.027-.216.324-.437.893-.663 3.498-1.524 5.831-2.529 6.998-3.015 3.333-1.386 4.025-1.627 4.477-1.635.099-.002.321.023.465.141.121.099.155.232.17.325.015.093.033.306.019.472z"/>
                        </svg>
                        <span className="text-white text-[16px] font-semibold">Share via Telegram</span>
                    </button>
                    <p className="text-center text-[#959595] text-[12px] mt-3">
                        Opens Telegram to select friends
                    </p>
                </div>
                
                {/* Copy link button */}
                <div className="px-4 py-3">
                    <button 
                        onClick={handleCopyLink}
                        className="w-full py-3 text-[#FFC813] text-[15px] font-medium"
                    >
                        {copied ? '✓ Link copied!' : 'Copy link'}
                    </button>
                </div>
                
                {/* Close button */}
                <div className="px-4 pb-6">
                    <button 
                        onClick={onClose}
                        className="w-full py-3 bg-[#252525] rounded-[12px] text-white text-[15px] font-medium"
                    >
                        Close
                    </button>
                </div>
            </div>
        </>
    );
};

// Получить favicon URL из ссылки
const getFaviconUrl = (url) => {
    if (!url) return null;
    try {
        const domain = new URL(url).hostname;
        return `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;
    } catch {
        return null;
    }
};

// Компонент для задачи из API
const PromoTaskItem = ({ task, onComplete, completed = false, isLast = false }) => {
    const faviconUrl = getFaviconUrl(task.details);
    const [completing, setCompleting] = useState(false);
    const [justCompleted, setJustCompleted] = useState(false);
    const isDone = completed || justCompleted;
    
    const handleGoClick = async () => {
        // Если есть ссылка в details - открыть её
        if (task.details && (task.details.startsWith('http') || task.details.startsWith('https'))) {
            window.open(task.details, '_blank', 'noopener,noreferrer');
        }
        if (!isDone && onComplete) {
            setCompleting(true);
            try {
                await onComplete(task.id);
                // Небольшая задержка перед показом галочки — для эффекта
                await new Promise(r => setTimeout(r, 400));
                setJustCompleted(true);
            } catch {
                // ошибка — кнопка вернётся
            } finally {
                setCompleting(false);
            }
        }
    };
    
    return (
        <div 
            className={`flex flex-row items-center justify-between w-full py-4 px-2 ${isLast ? '' : 'border-b border-[#353232]'}`}
            style={{
                transition: 'background-color 0.5s ease',
                backgroundColor: isDone ? 'rgba(74, 222, 128, 0.04)' : 'transparent',
            }}
        >
            <div className={'flex flex-row items-center justify-start gap-4 flex-1 min-w-0'}>
                {faviconUrl ? (
                    <div 
                        className={'w-[40px] h-[40px] min-w-[40px] rounded-full overflow-hidden'}
                        style={{ transition: 'opacity 0.3s ease', opacity: isDone ? 0.6 : 1 }}
                    >
                        <img 
                            src={faviconUrl} 
                            alt="site icon" 
                            className="w-full h-full object-cover rounded-full"
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.parentElement.outerHTML = `<div class="w-[40px] h-[40px] min-w-[40px] rounded-full bg-[#353232] flex items-center justify-center"><span class="text-[16px]">📋</span></div>`;
                            }}
                        />
                    </div>
                ) : (
                    <div className={'w-[40px] h-[40px] min-w-[40px] rounded-full bg-[#353232] flex items-center justify-center'}>
                        <span className={'text-[16px]'}>📋</span>
                    </div>
                )}
                <div className={'flex flex-col min-w-0'}>
                    <span 
                        className={'text-[15px] font-semibold truncate'}
                        style={{
                            transition: 'color 0.5s ease, text-decoration 0.5s ease',
                            color: isDone ? '#4ade80' : '#EAEAEA',
                            textDecoration: isDone ? 'line-through' : 'none',
                        }}
                    >
                        {task.title}
                    </span>
                    {task.details && (
                        <span className={'text-[#959595] text-[11px] truncate'}>{task.details}</span>
                    )}
                </div>
            </div>
            
            {/* Кнопка Go / Loading / Галочка */}
            <div 
                className={'flex flex-row items-center justify-center rounded-[10px] w-[56px] h-[36px] flex-shrink-0 cursor-pointer'}
                style={{
                    transition: 'background-color 0.4s ease, transform 0.3s ease',
                    backgroundColor: isDone ? '#2a5a2a' : completing ? '#b8a20e' : '#FFC813',
                    transform: justCompleted ? 'scale(1.1)' : 'scale(1)',
                }}
                onClick={!isDone ? handleGoClick : undefined}
            >
                {completing ? (
                    /* Спиннер */
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ animation: 'spin 0.8s linear infinite' }}>
                        <circle cx="12" cy="12" r="10" stroke="#000" strokeWidth="3" strokeLinecap="round" strokeDasharray="31.4 31.4" />
                    </svg>
                ) : isDone ? (
                    /* Галочка с анимацией */
                    <svg 
                        width="20" height="20" viewBox="0 0 24 24" fill="none" 
                        style={{ 
                            animation: justCompleted ? 'checkPop 0.4s ease-out' : 'none',
                        }}
                    >
                        <path 
                            d="M5 13l4 4L19 7" 
                            stroke="#4ade80" 
                            strokeWidth="3" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                            style={{
                                strokeDasharray: 24,
                                strokeDashoffset: justCompleted ? 0 : 0,
                                animation: justCompleted ? 'checkDraw 0.4s ease-out' : 'none',
                            }}
                        />
                    </svg>
                ) : (
                    <span className={'text-black text-[13px] font-semibold'}>Go</span>
                )}
            </div>
            
            {/* CSS анимации */}
            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                @keyframes checkPop {
                    0% { transform: scale(0); opacity: 0; }
                    50% { transform: scale(1.3); }
                    100% { transform: scale(1); opacity: 1; }
                }
                @keyframes checkDraw {
                    0% { stroke-dashoffset: 24; }
                    100% { stroke-dashoffset: 0; }
                }
            `}</style>
        </div>
    );
};

const TasksList = () => {
    const [isDefaultOpen, setIsDefaultOpen] = useState(true);
    const [isSocialOpen, setIsSocialOpen] = useState(true);
    const [showInviteModal, setShowInviteModal] = useState(false);
    
    // Реф ссылка для задачи «пригласи друга» — ТОЛЬКО ссылка-приглашение
    // (PROMO_INVITATION). Раньше при её отсутствии молча подставлялась
    // промоутерская promo.referralLink — по ней приглашение НЕ засчитывается,
    // и юзер копировал бесполезную ссылку, не подозревая об этом.
    const referralLink = promoReferralLink || '';
    
    const handleCompleteTask = async (taskId) => {
        if (!promoId) return;
        try {
            await TaskApi.completeTask(promoId, taskId);
            // Обновить прогресс и задачи
            await fetchProgress();
            const tasks = await TaskApi.getTasksByPromo(promoId);
            setPromoTasks(tasks || []);
        } catch (err) {
            console.error('[Tasks] complete error:', err);
        }
    };
    
    // Проверяем, выполнена ли задача из API (по прогрессу)
    const isTaskCompleted = (taskId) => {
        return completedTasksList.some(t => t.id === taskId);
    };

    return (
        <div className={'w-full h-full flex flex-col items-center border border-[#353232] rounded-[5px]'}>
            
            {/* Прогресс и Claim */}
            {!ownerBlocked && <ProgressBar />}
            
            {/* default */}
            <div
                className={'flex flex-row items-center border-b border-[#353232] w-full py-2 px-2 cursor-pointer'}
                onClick={() => setIsDefaultOpen(prev => !prev)}
            >
                <span className={'text-[11px] text-[#959595]'}>default</span>
            </div>
            {isDefaultOpen && (
                <DefaultTasks onOpenInviteModal={() => setShowInviteModal(true)} />
            )}

            {/* social media - задачи из API */}
            <div
                className={'flex flex-row items-center justify-between  w-full py-4 px-2 cursor-pointer'}
                onClick={() => setIsSocialOpen(prev => !prev)}
            >
                <span className={'text-[11px] text-[#959595]'}>social media</span>
                {promoTasks.length > 0 && (
                    <span className={'text-[10px] text-[#5BC2E7]'}>{promoTasks.length} tasks</span>
                )}
            </div>
            {isSocialOpen && (
                <div className={'w-full flex flex-col'}>
                    {tasksLoading ? (
                        <div className={'py-4 text-center text-[#959595] text-[12px]'}>Loading...</div>
                    ) : promoTasks.length === 0 ? (
                        <>
                            <TaskItem text={'Follow Neiro channel'} label={'telegram'} avatar={getIcon('dog')}/>
                            <TaskItem text={'Follow Neiro channel'} label={'twitter'} avatar={getIcon('dog')}/>
                        </>
                    ) : (
                        promoTasks.map((task, index) => (
                            <PromoTaskItem 
                                key={task.id} 
                                task={task} 
                                onComplete={handleCompleteTask}
                                completed={isTaskCompleted(task.id)}
                                isLast={index === promoTasks.length - 1}
                            />
                        ))
                    )}
                </div>
            )}

            {/* second chance (Claim Retry) — after failed claim */}
            {claimResult === 'failed' && canSecondChance && (
                <div className={'w-full flex flex-col items-center gap-[8px] px-[15px] py-[10px] border-b border-[#353232]'}>
                    <div 
                        className={`w-full h-[40px] bg-[#FFC813] rounded-[10px] flex flex-row items-center justify-center cursor-pointer gap-[8px] ${claiming ? 'opacity-50' : ''}`}
                        onClick={() => handleClaim(true)}
                    >
                        <img width={20} height={20} src={giftIcon} alt="gift" />
                        <span className={'text-black text-[14px] font-bold'}>
                            {claiming ? 'Claiming...' : 'Try again (2nd chance)'}
                        </span>
                    </div>
                    <span className={'text-[#959595] text-[10px] text-center'}>
                        You earned a second chance by inviting friends!
                    </span>
                </div>
            )}
            
            {/* Модалка приглашения */}
            <InviteModal 
                isOpen={showInviteModal} 
                onClose={() => setShowInviteModal(false)}
                referralLink={referralLink}
            />
        </div>
    );
}

    // Показываем loading пока проверяем статус
    if (checkingStatus) {
        return (
            <div className={'w-full flex flex-col items-center justify-center py-8'}>
                <span className={'text-[#959595] text-[12px]'}>Checking status...</span>
            </div>
        );
    }

    return(
        <div className={'w-full flex flex-col '}>
            {/* Владелец или подписанный видит TasksList, остальные - Sub для подписки */}
            {(isOwner || isSubscribe) ? <TasksList/> : <Sub/>}
            
            {/* Roulette animation on successful claim */}
            <ClaimRoulette
                isOpen={showRoulette}
                onClose={handleRouletteClose}
                positions={promoPositions}
                crystals={loadedCrystals}
                promoType={promoType}
                tokenIcon={tokenIconUrl}
                tokenTicker={tokenTicker}
                winnerPosition={rouletteData?.winnerPosition || 1}
                winnerAmount={rouletteData?.winnerAmount || 0}
            />
        </div>
    )
}
